import React, { useCallback, useState } from "react";
import { Alert, AlertTitle, Box, Button, CircularProgress } from "@mui/material";

import { Progress } from "ui";

import { apiSlice } from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { setVersion } from "fond/project/redux";
import { WorkflowKey } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, LogViewerModal } from "fond/widgets";

const stageConfig: Record<string, { label: string }> = {
  partition: { label: "Partitioning data" },
  prepare: { label: "Preparing inputs" },
  bom: { label: "Counting materials and costs" },
  c2s: { label: "Calculating cost to serve" },
};

interface DataModificationPanelProps {
  workflowKey: WorkflowKey;
}

const DataModificationPanel: React.FC<DataModificationPanelProps> = ({ workflowKey }) => {
  const dispatch = useAppDispatch();
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const versionId = useAppSelector((state) => state.project.versionId);
  const projectId = useAppSelector((state) => state.project.projectId);
  const { workflowState, stages, isLoading, logs } = useCustomWorkflow({
    versionId,
    key: workflowKey,
    onComplete: async () => {
      const { getVersions } = apiSlice.endpoints as any;

      // Invalidate the Versions tag to ensure we fetch the latest versions
      await dispatch(apiSlice.util.invalidateTags([{ type: "Versions", id: projectId }]));

      let result = await dispatch(getVersions.initiate(projectId, { forceRefetch: true }));
      let timeout = 10000;
      // Poll until loading finishes
      while (result.isLoading && timeout > 0) {
        await new Promise((resolve) => {
          setTimeout(resolve, 1000);
        });
        timeout -= 1000;
        result = await dispatch(getVersions.initiate(projectId, { forceRefetch: true }));
      }
      const versions = result.data;

      // Navigate to the latest version
      const latestVersion = versions?.ids && versions.ids.length > 1 ? versions.ids[0] : null;
      if (latestVersion) {
        dispatch(setVersion(latestVersion));
      }
    },
  });
  const { value: isCustomWorkflowErrorLogsEnabled } = useFeatureFlag("custom_workflow_error_logs");
  const handleViewLogs = useCallback(() => setIsLogsModalOpen(true), []);
  const isGeneratingCTS = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  if (isLoading) return <BlockSpinner />;

  return (
    <>
      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Cost to serve generation failed</AlertTitle>
          Cost to serve could not be generated.
          {isCustomWorkflowErrorLogsEnabled && (
            <Box mt={1}>
              {logs?.type === "waiting" && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={12} color="inherit" />
                  Attempting to process logs
                </Box>
              )}
              {logs?.type === "ready" && logs.logs.length === 0 && "Error logs are not available."}
              {logs?.type === "ready" && logs.logs.length > 0 && (
                <Button size="small" variant="outlined" color="error" onClick={handleViewLogs}>
                  View Logs
                </Button>
              )}
            </Box>
          )}
        </Alert>
      )}

      {stages && isGeneratingCTS && (
        <Alert sx={{ mb: 1 }} severity="info">
          <AlertTitle>Generating new scenario</AlertTitle>
        </Alert>
      )}

      {stages && (isGeneratingCTS || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key].label, Status: status }))} />
      )}

      {isCustomWorkflowErrorLogsEnabled && logs?.type === "ready" && (
        <LogViewerModal
          open={isLogsModalOpen}
          onClose={() => setIsLogsModalOpen(false)}
          logs={logs.logs}
          title="Logs"
          aria-label="Error logs modal"
        />
      )}
    </>
  );
};

export default DataModificationPanel;
